import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, StatusPill } from "@/components/Bits";
import { inputCls, primaryBtnCls, ghostBtnCls, Field, Modal } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const MODES = ["cash", "upi", "card", "insurance", "bank_transfer", "online", "cheque", "other"];

export default function LabBillDetail() {
  const { billId } = useParams();
  const location = useLocation();
  const { can } = useAuth();
  const [bill, setBill] = useState(null);
  const [sigs, setSigs] = useState([]);
  const [error, setError] = useState("");
  const [pay, setPay] = useState({ mode: "cash", amount: "", reference: "" });
  // Stable idempotency key per payment attempt: generated when the user starts a
  // payment entry and reused across retries (e.g. timeout → retry) so a double
  // submit cannot double-charge. Regenerated only after a recorded payment.
  const [payKey, setPayKey] = useState(() => `lab-bill-${billId}-${Date.now()}`);
  const [sigId, setSigId] = useState("");
  const [results, setResults] = useState({});
  const [workflow, setWorkflow] = useState("enabled");
  const [wa, setWa] = useState({ open: false, kind: "bill", templates: [], templateId: "", recipient: "patient", custom: "", busy: false });

  const openWa = async (kind) => {
    try {
      const { data } = await api.get("/whatsapp/report-options", { params: { kind } });
      setWa({ open: true, kind, templates: data.templates || data.items || [], templateId: "", recipient: "patient", custom: "", busy: false });
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendWa = async () => {
    if (!wa.templateId) { toast.error("Choose a template."); return; }
    setWa((s) => ({ ...s, busy: true }));
    try {
      const link = bill.report_token ? `${process.env.REACT_APP_BACKEND_URL}/api/public/lab-bill/${bill.report_token}` : "";
      const { data } = await api.post("/whatsapp/send-report", {
        template_id: wa.templateId, recipient_type: wa.recipient,
        patient_phone: wa.recipient === "patient" ? bill.patient_phone : "",
        custom_number: wa.recipient === "custom" ? wa.custom : "",
        ref_type: wa.kind === "report" ? "lab_report" : "lab_bill", ref_id: billId,
        context: { patient_name: bill.patient_name, reg_no: bill.reg_no, amount: String(bill.net_total), due: String(bill.due), report_link: link },
      });
      toast[data.log.status === "sent" ? "success" : "info"](`WhatsApp ${wa.kind}: ${data.log.status}${data.log.error ? " — " + data.log.error : ""}`);
      setWa((s) => ({ ...s, open: false, busy: false }));
    } catch (e) { toast.error(apiError(e)); setWa((s) => ({ ...s, busy: false })); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/lab/bills/${billId}`);
      setBill(data.bill);
      const r = {};
      (data.bill.result_items || []).forEach((row) => {
        r[row.rid] = row.test_type === "multiparameter"
          ? { parameters: row.parameters.map((p) => ({ name: p.name, result_value: p.result_value, flag: p.flag })) }
          : { result_value: row.result_value, flag: row.flag };
      });
      setResults(r);
    } catch (e) { setError(apiError(e)); }
  }, [billId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (can("lab.report.sign")) api.get("/lab/signatures", { params: { active: true } }).then(({ data }) => setSigs(data.items)).catch(() => {}); }, [can]);
  useEffect(() => { api.get("/lab/settings").then(({ data }) => setWorkflow(data.report_workflow || "enabled")).catch(() => {}); }, []);
  useEffect(() => { if (bill && location.state?.openWa) { openWa(location.state.openWa); window.history.replaceState({}, ""); } /* eslint-disable-next-line */ }, [bill]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const addPayment = () => act(() => api.post(`/lab/bills/${billId}/payment`, { mode: pay.mode, amount: Number(pay.amount), reference: pay.reference, idempotency_key: payKey }).then(() => { setPay({ mode: "cash", amount: "", reference: "" }); setPayKey(`lab-bill-${billId}-${Date.now()}`); }), "Payment recorded.");
  const saveResults = (submit) => {
    const rows = Object.entries(results).map(([rid, v]) => ({ rid, ...v }));
    return act(() => api.put(`/lab/bills/${billId}/results`, { rows, submit }), submit ? "Results submitted." : "Draft saved.");
  };

  if (!can("lab.orders.view")) return <NoAccess what="lab bills" />;
  if (error) return <ErrorState message={error} />;
  if (!bill) return <Loading />;

  const locked = ["verified", "signed_off", "released"].includes(bill.status);

  return (
    <div data-testid="lab-bill-detail">
      <PageHeader overline={`Reg. ${bill.reg_no}`} title={`${bill.patient_title} ${bill.patient_name}`}
        subtitle={`${bill.patient_uhid} · ${bill.patient_age_display} · ${bill.patient_gender} · ${bill.patient_phone}`}
        actions={<StatusPill status={bill.cancelled ? "cancelled" : bill.status} />} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="panel p-5">
            <p className="overline text-slate-400 mb-3">Investigations</p>
            <table className="w-full text-sm"><tbody>
              {bill.items.map((i) => (<tr key={i.item_id} className="border-b border-slate-50"><td className="py-2">{i.name}<span className="text-xs text-slate-400 capitalize"> · {i.kind}</span></td><td className="py-2 text-right text-slate-500">{i.qty} × {rup(i.price)}</td><td className="py-2 text-right font-medium">{rup(i.net)}</td></tr>))}
            </tbody></table>
          </div>

          {/* Sample */}
          {workflow === "enabled" && (
          <div className="panel p-5" data-testid="sample-panel">
            <p className="overline text-slate-400 mb-3">Sample</p>
            <div className="flex flex-wrap gap-2 text-sm">
              {can("lab.sample.collect") && !bill.sample?.collected && !bill.cancelled && <button className={ghostBtnCls} data-testid="collect-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/collect`, {}), "Sample collected.")}>Mark collected</button>}
              {can("lab.sample.collect") && bill.sample?.collected && !bill.sample?.received && <button className={ghostBtnCls} data-testid="receive-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/receive`, {}), "Sample received.")}>Mark received</button>}
              <span className="text-slate-500 py-2">{bill.sample?.received ? "Received ✓" : bill.sample?.collected ? "Collected ✓" : "Not collected"}</span>
            </div>
          </div>
          )}

          {/* Results */}
          {workflow === "enabled" && can("lab.results.enter") && (
            <div className="panel p-5" data-testid="results-panel">
              <p className="overline text-slate-400 mb-3">Result entry</p>
              <div className="space-y-3">
                {bill.result_items.map((row) => (
                  <div key={row.rid} className="border-b border-slate-50 pb-3" data-testid={`result-row-${row.rid}`}>
                    <p className="text-sm font-medium text-slate-800">{row.name} <span className="text-xs text-slate-400">{row.ref_range}</span></p>
                    {row.test_type === "multiparameter" ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                        {row.parameters.map((p, pi) => (
                          <input key={pi} className={inputCls} placeholder={`${p.name} (${p.unit})`} disabled={locked}
                            value={results[row.rid]?.parameters?.[pi]?.result_value ?? ""}
                            onChange={(e) => setResults((s) => { const c = { ...s }; c[row.rid].parameters[pi].result_value = e.target.value; return c; })}
                            data-testid={`param-${row.rid}-${pi}`} />
                        ))}
                      </div>
                    ) : (
                      <input className={`${inputCls} mt-2`} placeholder={`Result (${row.unit})`} disabled={locked}
                        value={results[row.rid]?.result_value ?? ""}
                        onChange={(e) => setResults((s) => ({ ...s, [row.rid]: { ...s[row.rid], result_value: e.target.value } }))}
                        data-testid={`result-${row.rid}`} />
                    )}
                  </div>
                ))}
              </div>
              {!locked && <div className="flex gap-2 mt-4"><button className={ghostBtnCls} onClick={() => saveResults(false)} data-testid="save-results-btn">Save draft</button><button className={primaryBtnCls} onClick={() => saveResults(true)} data-testid="submit-results-btn">Submit for verification</button></div>}
            </div>
          )}

          {/* Verify / Sign / Release */}
          <div className="panel p-5" data-testid="report-panel">
            <p className="overline text-slate-400 mb-3">{workflow === "enabled" ? "Verification & report" : "Report (external partner)"}</p>
            {workflow === "disabled" && <p className="text-xs text-slate-500 mb-2" data-testid="lab-billing-only-note">This Lab operates as a billing / collection point. The external diagnostic partner provides the report; technical sample, result and release steps are disabled.</p>}
            {workflow === "enabled" && (
            <div className="flex flex-wrap gap-2 items-center">
              {can("lab.results.verify") && bill.status === "result_entered" && <button className={primaryBtnCls} data-testid="verify-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/verify`, {}), "Verified.")}>Verify</button>}
              {can("lab.results.verify") && locked && <button className={ghostBtnCls} data-testid="reopen-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/reopen`, {}), "Reopened.")}>Reopen</button>}
              {can("lab.report.sign") && ["verified", "signed_off"].includes(bill.status) && (
                <>
                  <select className={`${inputCls} max-w-[200px]`} value={sigId} onChange={(e) => setSigId(e.target.value)} data-testid="sign-select"><option value="">Choose signatory…</option>{sigs.map((s) => <option key={s.signature_id} value={s.signature_id}>{s.signed_by}</option>)}</select>
                  <button className={ghostBtnCls} disabled={!sigId} data-testid="sign-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/sign`, { signature_id: sigId }), "Report signed.")}>Sign</button>
                </>
              )}
              {can("lab.report.release") && ["verified", "signed_off"].includes(bill.status) && <button className={primaryBtnCls} data-testid="release-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/release`, {}), "Report released.")}>Release report</button>}
              {can("lab.report.release") && bill.report_released && <button className={ghostBtnCls} data-testid="revoke-btn" onClick={() => act(() => api.post(`/lab/bills/${billId}/revoke`, {}), "Report revoked.")}>Revoke</button>}
              {can("lab.report.print") && ["result_entered", "verified", "signed_off", "released"].includes(bill.status) && (
                <a className={ghostBtnCls} target="_blank" rel="noreferrer" data-testid="preview-report-btn"
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/lab/bills/${billId}/report.pdf`}>Preview / Print report</a>
              )}
            </div>
            )}
            {bill.report_released && bill.report_token && (
              <p className="text-sm text-slate-600 mt-3">Public report: <a className="underline" target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/public/lab-bill/${bill.report_token}`} data-testid="public-report-link">Open PDF ↗</a></p>
            )}
            {bill.signatory?.signed_by && <p className="text-xs text-slate-500 mt-2">Signed by {bill.signatory.signed_by} ({bill.signatory.designation})</p>}
            {can("notifications.view") && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <button className={ghostBtnCls} onClick={() => openWa("bill")} data-testid="wa-bill-btn">WhatsApp Bill</button>
                {bill.report_released && !bill.report_revoked && <button className={ghostBtnCls} onClick={() => openWa("report")} data-testid="wa-report-btn">WhatsApp Report</button>}
              </div>
            )}
          </div>
        </div>

        <Modal open={wa.open} onClose={() => setWa((s) => ({ ...s, open: false }))} title={`WhatsApp ${wa.kind}`} testid="wa-modal">
          <div className="space-y-4">
            <Field label="Template"><select className={inputCls} value={wa.templateId} onChange={(e) => setWa((s) => ({ ...s, templateId: e.target.value }))} data-testid="wa-template"><option value="">Choose template…</option>{wa.templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.active ? "" : " (inactive)"}</option>)}</select></Field>
            <Field label="Recipient"><select className={inputCls} value={wa.recipient} onChange={(e) => setWa((s) => ({ ...s, recipient: e.target.value }))} data-testid="wa-recipient"><option value="patient">Patient ({bill.patient_phone || "no number"})</option><option value="custom">Custom number</option></select></Field>
            {wa.recipient === "custom" && <Field label="Number"><input className={inputCls} value={wa.custom} onChange={(e) => setWa((s) => ({ ...s, custom: e.target.value }))} data-testid="wa-custom" /></Field>}
            <p className="text-xs text-slate-500">Delivery is logged and respects report release/signature. In preview environments Interakt is queued as “pending”.</p>
            <div className="flex justify-end gap-3"><button className={ghostBtnCls} onClick={() => setWa((s) => ({ ...s, open: false }))}>Cancel</button><button disabled={wa.busy} className={primaryBtnCls} onClick={sendWa} data-testid="wa-send-btn">{wa.busy ? "Sending…" : "Send"}</button></div>
          </div>
        </Modal>

        {/* Billing sidebar */}
        <div className="space-y-4">
          <div className="panel p-5">
            <p className="overline text-slate-400 mb-3">Billing</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{rup(bill.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Discount</span><span>-{rup(bill.total_discount)}</span></div>
              <div className="flex justify-between text-slate-600"><span>Collection + Tax</span><span>{rup((bill.collection_charge || 0) + (bill.tax || 0))}</span></div>
              <div className="flex justify-between font-semibold text-slate-900 pt-1"><span>Net total</span><span>{rup(bill.net_total)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>Paid</span><span data-testid="detail-paid">{rup(bill.paid)}</span></div>
              <div className="flex justify-between text-amber-700"><span>Due</span><span data-testid="detail-due">{rup(bill.due)}</span></div>
            </div>
            {can("lab.billing.manage") && bill.due > 0 && !bill.cancelled && (
              <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <select className={inputCls} value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })} data-testid="detail-pay-mode">{MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}</select>
                  <input type="number" className={inputCls} placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="detail-pay-amount" />
                </div>
                <input className={inputCls} placeholder="Reference / txn no." value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
                <button className={`${primaryBtnCls} w-full`} onClick={addPayment} data-testid="detail-pay-btn">Record payment</button>
                <button className={`${ghostBtnCls} w-full`} onClick={() => act(() => api.post(`/lab/bills/${billId}/accept-due`, {}), "Due accepted.")} data-testid="accept-due-btn">Accept due (pay later)</button>
              </div>
            )}
          </div>
          <div className="panel p-5">
            <p className="overline text-slate-400 mb-3">Payment history</p>
            <div className="space-y-2 text-sm">
              {(bill.payments || []).length === 0 ? <p className="text-slate-400">No payments yet.</p> :
                bill.payments.map((p) => (<div key={p.payment_id} className="flex justify-between" data-testid={`pay-hist-${p.payment_id}`}><span className="capitalize text-slate-600">{p.mode.replace("_", " ")} {p.kind === "refund" ? "(refund)" : ""}</span><span className={p.kind === "refund" ? "text-red-600" : "text-slate-900"}>{p.kind === "refund" ? "-" : ""}{rup(p.amount)}</span></div>))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
